import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from 'api';
import { chooseProfile, onActiveProfileChange } from 'utils/activeProfile';

const mealKey = (meal) => (meal === 'Snacks' ? 'Snack' : meal);

/**
 * The signed-in profile's per-meal targets as stored in Airtable, the same
 * figures the account panel shows. Null for guests, before a profile is picked,
 * or when the lookup fails, so callers fall back to the quiz/default numbers.
 * @returns {{hideNutrition: boolean, targets: object, skippedMeals: string[], snacksPerDay: number|null}|null}
 */
export function useAccountNutrition() {
  const { isAuthenticated, isLoading } = useAuth0();
  const [result, setResult] = useState(null);
  const [profileVersion, setProfileVersion] = useState(0);

  useEffect(() => onActiveProfileChange(() => setProfileVersion((v) => v + 1)), []);

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      setResult(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: auth } = await api.get('/auth/token', { timeout: 15000 });
        const profile = chooseProfile(auth);
        if (!profile?.token) throw new Error('no profile');
        const { data } = await api.get(`/orders/${profile.token}/account-overview`, { timeout: 25000 });
        if (!data?.success) throw new Error('no overview');
        const targets = {};
        const skippedMeals = [];
        let snacksPerDay = null;
        (data.nutritionByMeal || []).forEach((m) => {
          if (!m?.meal) return;
          if (m.skipped) { skippedMeals.push(mealKey(m.meal)); return; }
          targets[mealKey(m.meal)] = m;
          if (mealKey(m.meal) === 'Snack' && m.snacksPerDay > 0) snacksPerDay = m.snacksPerDay;
        });
        if (!cancelled) setResult({ hideNutrition: Boolean(data.hideNutrition), targets, skippedMeals, snacksPerDay });
      } catch {
        if (!cancelled) setResult(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, isLoading, profileVersion]);

  return result;
}
